import clsx from 'clsx';
import { toast } from 'react-toastify';
import { exportEditorToPdfServer } from './exporters/exportToPdfServer';
import { ProgressBorder } from './ProgressBorder';
import { ExportingLabel } from './ExportingLabel';
import { useExportProgress } from './hooks/useExportProgress';
import { usePnoReportLogoResolver } from '../Reports/usePnoReportLogo';
import styles from './PreviewButton.module.scss';

// How long the blob object URL stays alive after the preview tab opens.
const PREVIEW_URL_TTL_MS = 30_000;
// How long the blob object URL stays alive while the popup-blocked fallback toast
// waits for the user to click through.
const POPUP_FALLBACK_URL_TTL_MS = 120_000;

interface OpenEditorPdfPreviewOptions {
    target: HTMLElement;
    filename: string;
    /** RECUR-27304 Delta v3 — mirrors `exportEditorToPdfServer`'s own option; see its doc. */
    injectModelPageBreaks?: boolean;
    /** PNO logo as a self-contained data: URI — mirrors `exportEditorToPdfServer`'s own option; see its doc. */
    pnoLogoDataUrl?: string;
}

// Render the same server PDF the user would download (with the exporter's own
// raster fallback) and open it in a new browser tab instead of saving to disk.
// The new tab is opened only after the blob resolves, so it has no fresh user
// gesture — the browser may block the popup, handled by the toast fallback.
export async function openEditorPdfPreview({
    target,
    filename,
    injectModelPageBreaks,
    pnoLogoDataUrl,
}: OpenEditorPdfPreviewOptions): Promise<void> {
    let blob: Blob | undefined;
    try {
        blob = (await exportEditorToPdfServer({ target, filename, returnBlob: true, injectModelPageBreaks, pnoLogoDataUrl })) as
            | Blob
            | undefined;
    } catch (err) {
        console.error('[openEditorPdfPreview] PDF preview export failed', err);
        toast.error('PDF preview failed. Please try again.');
        return;
    }
    if (!blob) {
        toast.error('PDF preview failed. Please try again.');
        return;
    }

    const url = URL.createObjectURL(blob);
    const tab = window.open(url, '_blank');
    if (tab) {
        setTimeout(() => URL.revokeObjectURL(url), PREVIEW_URL_TTL_MS);
        return;
    }

    // Popup blocked — re-opening from the toast button counts as a real user
    // gesture, so it won't be blocked. Keep the URL alive until the user acts.
    let revoked = false;
    const revoke = () => {
        if (revoked) return;
        revoked = true;
        URL.revokeObjectURL(url);
    };
    toast.error(
        ({ closeToast }) => (
            <div>
                <div>Preview is ready but your browser blocked the popup.</div>
                <button
                    type="button"
                    onClick={() => {
                        window.open(url, '_blank');
                        revoke();
                        closeToast?.();
                    }}
                    className={styles.popupFallbackBtn}
                >
                    Open preview
                </button>
            </div>
        ),
        { autoClose: false, closeOnClick: false },
    );
    setTimeout(revoke, POPUP_FALLBACK_URL_TTL_MS);
}

interface PreviewButtonProps {
    /**
     * Editor canvas root captured for the PDF. Pass a getter (not the resolved
     * `ref.current`) so the target is read at click time — `headerActions` is
     * built during render when the canvas ref may still be null.
     */
    getTarget: () => HTMLElement | null;
    /** Resolved (sanitized) export filename. */
    filename: string;
    disabled?: boolean;
    className?: string;
    /**
     * RECUR-27304 Delta v3: forwarded to `exportEditorToPdfServer` so the
     * Preview-tab PDF matches the downloaded one on the page-gap surfaces —
     * pass the SAME value the host passes its own "Export .pdf" call.
     */
    injectModelPageBreaks?: boolean;
    /**
     * RECUR-27604: resolve and forward the PNO logo, mirroring the host's own
     * doctype gate (`isPageGapDocumentType` — only `REPORTS`/`ONE_PAGER` may
     * carry a logo). Default `false` — this is a deliberate BEHAVIOUR FLIP:
     * before this flag existed, `PreviewButton` resolved the logo
     * unconditionally for every doctype (EL/NDA/consent/application form
     * included). Separate from `injectModelPageBreaks` on purpose — branding
     * and pagination-determinism are different concerns even though they
     * coincide today on the same two doctypes.
     */
    withPnoLogo?: boolean;
}

export function PreviewButton({
    getTarget,
    filename,
    disabled,
    className,
    injectModelPageBreaks,
    withPnoLogo = false,
}: PreviewButtonProps) {
    const { percent, isRunning, track } = useExportProgress();
    const resolvePnoLogo = usePnoReportLogoResolver();

    const handleClick = async () => {
        const target = getTarget();
        if (!target || isRunning) return;
        const pnoLogoDataUrl = withPnoLogo ? await resolvePnoLogo() : undefined;
        await track(() => openEditorPdfPreview({ target, filename, injectModelPageBreaks, pnoLogoDataUrl }));
    };

    if (isRunning) {
        return (
            <div className={clsx(styles.previewBtn, styles.exporting, className)} role="status" aria-live="polite">
                <ProgressBorder percent={percent} />
                <span>Generating…</span>
            </div>
        );
    }

    return (
        <button
            type="button"
            className={clsx(styles.previewBtn, className)}
            onClick={handleClick}
            disabled={disabled}
            aria-label="Preview PDF"
            title="Preview the PDF in a new tab (no download)"
        >
            Preview
        </button>
    );
}
